import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { ukDeadlineMs } from "./LaunchCountdown";

const DAY = 86400000;

export const LaunchHeaderBadge = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [info, setInfo] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Reuse the existing launch-summary endpoint; refetch on route change (picks up
  // launch-date / readiness edits) — no continuous backend polling.
  useEffect(() => {
    let active = true;
    api.list("launch-summary")
      .then((s) => { if (active) setInfo({ date: s.target_launch_date, readiness: s.readiness }); })
      .catch(() => {});
    return () => { active = false; };
  }, [pathname]);

  // Days change at most once per day; refresh the clock every minute, not every second.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  if (!info?.date) return null;
  const diff = ukDeadlineMs(info.date) - now;
  const r = info.readiness ?? 0;

  let label;
  if (diff <= 0) {
    const since = Math.floor((now - ukDeadlineMs(info.date)) / DAY);
    label = since < 1 ? "Launch Day" : `Launched ${since} day${since === 1 ? "" : "s"} ago`;
  } else {
    const days = Math.floor(diff / DAY);
    label = days === 0 ? "Launch Day" : `${days} day${days === 1 ? "" : "s"} to launch`;
  }

  return (
    <button
      data-testid="header-launch-badge"
      onClick={() => navigate("/launch")}
      title="View Launch Tracker"
      aria-label={`${label}, ${r} percent ready. View Launch Tracker`}
      className="hidden items-center gap-2 rounded-full border border-[#A5F3FC] bg-[#ECFEFF] px-3 py-1.5 text-xs font-bold text-[#0E7490] transition-colors hover:border-[#22D3EE] md:flex"
    >
      <Rocket size={13} className="text-[#0891B2]" aria-hidden="true" />
      <span className="whitespace-nowrap">{label}</span>
      <span aria-hidden="true" className="text-[#0E7490]/40">·</span>
      <span className="whitespace-nowrap text-[#0891B2]">{r}% ready</span>
    </button>
  );
};
